import React from "react";
import { ShoppingGallery } from "./shopping-gallery";
import ProductFiltersBar from "./filters-bar";
import SortBar from "./sort-bar";
import { cloneData } from "../../../library/utils/clone";
import { sortByProperty } from "../../functions/sort-by-property";
import { InfiniteScrollComponent } from "../../../library/components/components";
import LoadingIndicator from "../loading/loading-indicator";
import GallerySearchBar from "./gallery-search-bar";
import { parseFiltersFromUrl, writeFiltersToUrl, subscribePopstate, arraysShallowEqual, normalizeArray } from "../../functions/filter-url-state";

// URL ↔ filter state mapping for the Frame / Product gallery.
// Plural keys for multi-value (CSV) to avoid colliding with any future
// SearchController-style singular params — see docs/filter-url-state-plan.md.
var URL_SCHEMA = {
    industries: 'array', features: 'array', statuses: 'array',
    collections: 'array', products: 'array',
    priceMin: 'number', priceMax: 'number',
    widthMin: 'number', widthMax: 'number',
    heightMin: 'number', heightMax: 'number',
    depthMin: 'number', depthMax: 'number',
    armHeightMin: 'number', armHeightMax: 'number',
    seatHeightMin: 'number', seatHeightMax: 'number',
    seatDepthMin: 'number', seatDepthMax: 'number',
    sort: 'string', find: 'string'
};
var DIMENSION_KEYS = ['width', 'height', 'depth', 'armHeight', 'seatHeight', 'seatDepth'];
// Sort is a single token string here ("name|ASC"), not an array as in Search.
var DEFAULT_SORT = "name|ASC";
// `status` filter values aren't user-typed labels — they're code values like
// "inStock" / "new" / "clearance" / "commercialGrade". Hardcoded canonical
// list for case-insensitive normalization of URL-pasted values.
var STATUS_CANONICAL = ['new', 'inStock', 'clearance', 'commercialGrade'];

function urlToFilterBarState(u) {
    if (!u) return null;
    var hasAny = false;
    var out = {
        priceRange: {}, industry: [], features: [], status: [],
        collections: [], products: [], dimensions: {}
    };
    if (u.industries)  { out.industry    = u.industries;  hasAny = true; }
    if (u.features)    { out.features    = u.features;    hasAny = true; }
    if (u.statuses)    { out.status      = u.statuses;    hasAny = true; }
    if (u.collections) { out.collections = u.collections; hasAny = true; }
    if (u.products)    { out.products    = u.products;    hasAny = true; }
    if (u.priceMin != null) { out.priceRange.greaterThan = u.priceMin; hasAny = true; }
    if (u.priceMax != null) { out.priceRange.lessThan = u.priceMax; hasAny = true; }
    for (var i = 0; i < DIMENSION_KEYS.length; i++) {
        var dk = DIMENSION_KEYS[i];
        if (u[dk + 'Min'] != null) { out.dimensions[dk + 'Min'] = u[dk + 'Min']; hasAny = true; }
        if (u[dk + 'Max'] != null) { out.dimensions[dk + 'Max'] = u[dk + 'Max']; hasAny = true; }
    }
    return hasAny ? out : null;
}

function filterBarStateToUrl(s) {
    if (!s) return {};
    var out = {
        industries: s.industry, features: s.features, statuses: s.status,
        collections: s.collections, products: s.products
    };
    if (s.priceRange) {
        if (s.priceRange.greaterThan != null) out.priceMin = s.priceRange.greaterThan;
        if (s.priceRange.lessThan != null) out.priceMax = s.priceRange.lessThan;
    }
    if (s.dimensions) {
        for (var i = 0; i < DIMENSION_KEYS.length; i++) {
            var dk = DIMENSION_KEYS[i];
            if (s.dimensions[dk + 'Min'] != null) out[dk + 'Min'] = s.dimensions[dk + 'Min'];
            if (s.dimensions[dk + 'Max'] != null) out[dk + 'Max'] = s.dimensions[dk + 'Max'];
        }
    }
    return out;
}

function normalizeUrlState(parsed, serverFilters) {
    if (!parsed) return parsed;
    var f = serverFilters || {};
    if (parsed.industries)  parsed.industries  = normalizeArray(parsed.industries,  f.industries);
    if (parsed.features)    parsed.features    = normalizeArray(parsed.features,    f.features);
    if (parsed.collections) parsed.collections = normalizeArray(parsed.collections, f.collections);
    if (parsed.products)    parsed.products    = normalizeArray(parsed.products,    f.productTypes);
    if (parsed.statuses)    parsed.statuses    = normalizeArray(parsed.statuses,    STATUS_CANONICAL);
    return parsed;
}

// Variant products — the "Available in Leather" L- twins and the
// "Available with Casters" caster twins — are HIDDEN from galleries by
// default and only revealed when their corresponding Feature filter is
// selected (client request 2026-07-13: they read as duplicate listings
// next to their base products). The feature tags mark exactly the
// variant products, not the base ones (see GetProductFeatures in
// Fairfield.Entities/Extensions — LEATHERAVL+L- prefix, CASTERS2 XOR
// CASTERS4), so tag presence == variant.
var VARIANT_FEATURES = ["Available in Leather", "Available with Casters"];
function variantVisible(product, selectedFeatures) {
    for (var i = 0; i < VARIANT_FEATURES.length; i++) {
        var vf = VARIANT_FEATURES[i];
        if ((product.features || []).indexOf(vf) !== -1
            && (!selectedFeatures || selectedFeatures.indexOf(vf) === -1)) {
            return false;
        }
    }
    return true;
}

// Shared filter predicate — extracted to module scope so applyUrlState and
// onFiltersChange share one implementation.
function productMatchesFilters(product, f) {
    if (!variantVisible(product, f.features)) return false;
    if (f.industry && f.industry.length) {
        var okIndustry = false;
        for (var i = 0; i < f.industry.length; i++) {
            if ((product.industries || []).indexOf(f.industry[i]) !== -1) { okIndustry = true; break; }
        }
        if (!okIndustry) return false;
    }
    if (f.collections && f.collections.length) {
        if (f.collections.indexOf(product.collection || "") === -1) return false;
    }
    if (f.features && f.features.length) {
        var okFeatures = false;
        for (var j = 0; j < f.features.length; j++) {
            if ((product.features || []).indexOf(f.features[j]) !== -1) { okFeatures = true; break; }
        }
        if (!okFeatures) return false;
    }
    if (f.status && f.status.length) {
        var okStatus = false;
        for (var q = 0; q < f.status.length; q++) {
            var val = f.status[q];
            if (val === "inStock" && product.inStock === true) { okStatus = true; break; }
            if (val === "new" && product.badge === "New") { okStatus = true; break; }
            if (val === "clearance" && product.status === "PROMO/TD") { okStatus = true; break; }
            if (val === "commercialGrade" && product.grade === "Commercial Grade") { okStatus = true; break; }
        }
        if (!okStatus) return false;
    }
    if (f.products && f.products.length) {
        if (f.products.indexOf(product.prodCat || "") === -1) return false;
    }
    if (f.dimensions) {
        for (var di = 0; di < DIMENSION_KEYS.length; di++) {
            var dk = DIMENSION_KEYS[di];
            var dMin = f.dimensions[dk + "Min"];
            var dMax = f.dimensions[dk + "Max"];
            if (dMin != null || dMax != null) {
                var val = product[dk];
                if (val == null) return false;
                if (dMin != null && val < dMin) return false;
                if (dMax != null && val > dMax) return false;
            }
        }
    }
    if (f.priceRange) {
        var pGt = f.priceRange.greaterThan;
        var pLt = f.priceRange.lessThan;
        if (pGt != null || pLt != null) {
            var pv = product.priceValue;
            if (!pv) return false;
            if (pGt != null && pv < pGt) return false;
            if (pLt != null && pv > pLt) return false;
        }
    }
    return true;
}


class Gallery extends React.Component {
    constructor(props) {
        super(props);
        this.state = { mobileFilterOpen: false };
    }

    openMobileFilter = () => { this.setState({ mobileFilterOpen: true }); };
    closeMobileFilter = () => { this.setState({ mobileFilterOpen: false }); };

    render() {
        let data = this.props.data;
        let count = data.length;
        let countLabel = count === 1 ? "item" : "items";
        let displayCount = this.props.pagedData.length;
        return (
            <div>
                <div className="gallery-grid" ref={(ref) => this.galleryGrid = ref}>
                    <div className="gallery-filters-container">

                    </div>
                    <div className="gallery-items-container">
                        <div className="filters-header-container">
                            <div className="filters-bar-small-screen">
                                <span onClick={this.openMobileFilter}>Filter</span>
                            </div>
                            <div className={"filters-bar-container" + (this.state.mobileFilterOpen ? " mobile-open" : "")}>
                                <div className="mobile-filter-header">
                                    <span className="mobile-filter-title">FILTER/SORT</span>
                                    <button type="button" className="mobile-filter-close" onClick={this.closeMobileFilter}>×</button>
                                </div>
                                <div className="mobile-filter-body">
                                    {this.props.children}
                                    <span className="sort-bar-mobile-only">
                                        <SortBar
                                            key={"sbm-" + (this.props.urlVersion || 0)}
                                            onSortChange={this.props.onSortChange}
                                            excludePriceSort={!this.props.isSignedIn}
                                            defaultSort={this.props.currentSort || "name|ASC"}
                                        />
                                    </span>
                                </div>
                                <div className="mobile-filter-footer">
                                    <span className="mobile-filter-count">{count} {count === 1 ? "Piece" : "Pieces"}</span>
                                    <button type="button" className="mobile-filter-apply" onClick={this.closeMobileFilter}>APPLY</button>
                                </div>
                            </div>
                            {this.state.mobileFilterOpen && <div className="mobile-filter-overlay" onClick={this.closeMobileFilter}></div>}
                            <div className="gallery-items-count">
                                <GallerySearchBar
                                    key={"gsb-" + (this.props.urlVersion || 0)}
                                    onSubmitSearch={this.props.onSubmitSearch}
                                    initialValue={this.props.searchTerm || ""}
                                />
                                <span className="sort-bar-desktop-only">
                                    <SortBar
                                        key={"sbd-" + (this.props.urlVersion || 0)}
                                        onSortChange={this.props.onSortChange}
                                        excludePriceSort={!this.props.isSignedIn}
                                        defaultSort={this.props.currentSort || "name|ASC"}
                                    />
                                </span>
                                <span className="count-label">{displayCount}&nbsp;of&nbsp;{count}&nbsp;{countLabel}</span>

                            </div>
                        </div>
                        <div className="gallery-items-list">
                            <ShoppingGallery
                                data={this.props.pagedData}
                                baseImageUrl={this.props.baseImageUrl}
                                onLike={this.props.onLike}
                                location={this.props.location}
                                isSignedIn={this.props.isSignedIn}
                            />
                            {count < 1 &&
                                <div className="gallery-no-results">
                                    No items match your filter criteria. Please adjust your filters and try again.
                                </div>
                            }
                        </div>
                    </div>
                </div>
                {this.props.showLoader &&
                    <LoadingIndicator />
                }
            </div>  
            
        );

    }
}

class BaseGalleryComponent extends InfiniteScrollComponent {
    constructor(props) {
        super(props)
        this.yBottom = 500;
        let data = props.data || [];
        data = sortByProperty(data, "name", "ASC");
        let dataCopy = cloneData(data);
        data=this.filterDataByIndustry(data,props.industry);
        // Default view (no filters yet — SSR + first client paint):
        // variant products hidden. dataCopy keeps the full universe so
        // selecting a Feature filter can reveal them.
        data = data.filter(function (p) { return variantVisible(p, null); });
        let N=100;
        let pagedData=data.slice(0,N);
        this.throttleScroll = false;
        this.state = {
            pagedData:pagedData, //paged filtered data for current view
            page:1, //current page
            N:N, //items per page
            maxPage:this.maxPage(data,N),// max pages
            originalMaxPage:this.maxPage(data,N),
            data: data, //current filtered data
            filteredDataCopy:data, //copy of current filtered data
            dataCopy: dataCopy, //original unfiltered data
            sortBy: DEFAULT_SORT,
            showLoader: false,
            searchTerm:'',
            // bumped on popstate to force ProductFiltersBar / SortBar /
            // GallerySearchBar to remount with new URL-driven initial values
            urlVersion: 0,
            // null until applyUrlState runs in componentDidMount; passed to
            // ProductFiltersBar as initialFilters and used by syncUrl
            initialFilterBarState: null
        }
        this._currentFilterBarState = null;
    }

    componentDidMount() {
        super.componentDidMount();
        this.toast = document.querySelector("paper-toast");
        this.galleryGrid = document.querySelector(".gallery-grid");
        this.applyUrlState();
        this._popstateUnsub = subscribePopstate(this.onPopstate);
    }

    componentWillUnmount() {
        if (typeof super.componentWillUnmount === 'function') super.componentWillUnmount();
        if (this._popstateUnsub) { this._popstateUnsub(); this._popstateUnsub = null; }
    }

    onPopstate = () => {
        this.applyUrlState({ bumpVersion: true });
    }

    applyUrlState = (opts) => {
        var parsed = parseFiltersFromUrl(URL_SCHEMA);
        parsed = normalizeUrlState(parsed, this.props.filters);
        var filterState = urlToFilterBarState(parsed);
        var sortBy = parsed.sort || null;
        var find = parsed.find || '';

        if (!filterState && !sortBy && !find) {
            if (opts && opts.bumpVersion) {
                this.setState({ urlVersion: (this.state.urlVersion || 0) + 1 });
            }
            return;
        }

        this._currentFilterBarState = filterState;

        var filtered = (this.state.dataCopy || []).slice();
        if (this.props.industry) filtered = this.filterDataByIndustry(filtered, this.props.industry);
        if (filterState) {
            filtered = filtered.filter(function (p) { return productMatchesFilters(p, filterState); });
        } else {
            // Sort-only / find-only URLs still get the default view —
            // variants hidden (productMatchesFilters handles the reveal
            // when a filterState carries the corresponding feature).
            filtered = filtered.filter(function (p) { return variantVisible(p, null); });
        }
        var activeSort = sortBy || this.state.sortBy || DEFAULT_SORT;
        filtered = this.sortData(filtered, activeSort);

        var filteredDataCopy = cloneData(filtered);
        if (find) {
            var self = this;
            filtered = filtered.filter(function (p) { return self.matchesSearch(p, find); });
        }

        var paged = filtered.slice(0, this.state.N);
        var nextState = {
            pagedData: paged,
            data: filtered,
            filteredDataCopy: filteredDataCopy,
            page: 1,
            maxPage: this.maxPage(filtered, this.state.N),
            sortBy: activeSort,
            searchTerm: find,
            initialFilterBarState: filterState
        };
        if (opts && opts.bumpVersion) {
            nextState.urlVersion = (this.state.urlVersion || 0) + 1;
        } else if (filterState || find || sortBy) {
            nextState.urlVersion = (this.state.urlVersion || 0) + 1;
        }
        this.setState(nextState);
    }

    syncUrl = (overrides) => {
        var sortBy = (overrides && overrides.sortBy != null) ? overrides.sortBy : (this.state.sortBy || DEFAULT_SORT);
        var searchTerm = (overrides && typeof overrides.searchTerm === 'string') ? overrides.searchTerm : (this.state.searchTerm || '');
        var filterBarState = (overrides && overrides.filterBarState !== undefined) ? overrides.filterBarState : this._currentFilterBarState;
        var urlState = filterBarStateToUrl(filterBarState);
        // Only emit `sort` when it differs from the gallery default.
        if (sortBy && sortBy !== DEFAULT_SORT && !arraysShallowEqual([sortBy], [DEFAULT_SORT])) {
            urlState.sort = sortBy;
        }
        if (searchTerm) urlState.find = searchTerm;
        writeFiltersToUrl(urlState, URL_SCHEMA);
    }

    maxPage=(data,N)=>{
        return Math.ceil(data.length / N);
    }

    filterDataByIndustry=(data,industry)=>{
        if(!industry) return data;
        return data.filter(item=>{
            return (item.industries.includes(industry));
        });
    }

    // Clear All chip — reset filters AND sort so the URL goes clean.
    onClearAll = (clearedFilters) => {
        this._currentFilterBarState = clearedFilters;
        var data = this.state.dataCopy.slice();
        if (this.props.industry) data = this.filterDataByIndustry(data, this.props.industry);
        // Cleared filters = default view = variants hidden again.
        data = data.filter(function (p) { return variantVisible(p, null); });
        data = this.sortData(data, DEFAULT_SORT);
        let filteredDataCopy = cloneData(data);
        if (this.state.searchTerm && this.state.searchTerm.trim() !== '') {
            data = data.filter(p => this.matchesSearch(p, this.state.searchTerm));
        }
        this.setState({
            pagedData: data.slice(0, this.state.N),
            data: data,
            filteredDataCopy: filteredDataCopy,
            page: 1,
            maxPage: this.maxPage(data, this.state.N),
            sortBy: DEFAULT_SORT,
            urlVersion: (this.state.urlVersion || 0) + 1,
            showLoader: false
        });
        this.syncUrl({ filterBarState: clearedFilters, sortBy: DEFAULT_SORT });
    }

    onFiltersChange = (filters) => {
        this._currentFilterBarState = filters;
        var allProducts = this.state.dataCopy;
        var filtered = allProducts.filter(function (p) {
            return productMatchesFilters(p, filters);
        });

        if(this.state.sortBy){
            filtered = this.sortData(filtered,this.state.sortBy);
        }
        let filteredDataCopy=cloneData(filtered);
        if(this.state.searchTerm && this.state.searchTerm.trim() !==''){
            filtered = filtered.filter(product => this.matchesSearch(product, this.state.searchTerm));
        }
        var paged=filtered.slice(0,this.state.N);
        let maxPage=this.maxPage(filtered,this.state.N);

        this.setState({
            pagedData: paged,
            data: filtered,
            filteredDataCopy: filteredDataCopy,
            page:1,
            maxPage:maxPage,
            showLoader: false
        },()=>{
            this.throttleScroll = false;
        });
        this.syncUrl({ filterBarState: filters });
    }

    uniqueById = (arr) => {
        return [...new Map(arr.map(item => [item.id, item])).values()];
    }

    onLike = (e,item) => {
        e.preventDefault();
        e.stopPropagation();
        document.dispatchEvent(new CustomEvent('favorite-add', { detail: { itemType: 'Frame', sku: item.sku, name: item.name, image: this.props.baseImageUrl + '/' + item.image + '?w=500', collection: item.collection || '', link: item.link || window.location.pathname, price: item.price || '' } }));
    }

    sortData=(data,sortBy)=>{
        let params = sortBy.split("|");
        let sortField = params[0];
        let sortDirection = params[1];
        let sortedData = sortByProperty(data, sortField, sortDirection);
        return sortedData;
    }

    onSortChange = (sortBy) => {
        let val = Array.isArray(sortBy) ? sortBy[0] : sortBy;
        if (!val) return;
        let params = val.split("|");
        let sortField = params[0];
        let sortDirection = params[1];
        let sortedData = sortByProperty(this.state.data, sortField, sortDirection);
        let pagedData=sortedData.slice(0,this.state.N);
        this.setState({ data: sortedData,
            filteredDataCopy: sortedData,
            pagedData: pagedData,
            sortBy:val
        });
        this.syncUrl({ sortBy: val });
    }

    rebind=()=>{
        //this.galleryGrid.classList.add('lazy-image-disabled');
        let nextPage=this.state.page+1;
        let pagedData=this.state.data.slice(0,this.state.N*nextPage);
        console.log("Rebind:",nextPage,pagedData.length);
        this.setState({
            pagedData: pagedData,
            page: nextPage,
            showLoader: false
        },()=>{
            this.throttleScroll = false;
        });
        
    }

    onScroll() {
        super.onScroll();
        if (!this.throttleScroll) {
            this.throttleScroll = true;
            if (this.state.page < this.state.maxPage) {
                this.setState({ showLoader: true },()=>{
                    setTimeout(() => {
                        this.rebind();
                    }, 1000);
                });
            }
        }
    }

    onSubmitSearch = (value) => {
        console.log("Search submitted from Gallery:", value);
        // Implement search logic here based on the submitted value
        // For example, you might want to filter this.state.dataCopy and then update this.state.data
        /*this.setState((prevState) => {
            const nextState = { ...prevState, searchQuery: value };
            if (typeof this.props.onSearchSubmit === "function") {
                this.props.onSearchSubmit(nextState);     */          
                    
        var trimmed = value ? value.trim() : '';
        if(!trimmed){
            let data=this.state.filteredDataCopy;
            let pagedData=data.slice(0,this.state.N);
            this.setState({
                pagedData: pagedData,
                data: data,
                page:1,
                maxPage:this.maxPage(pagedData,this.state.N),
                searchTerm:''
            });
            this.syncUrl({ searchTerm: '' });
            return;
        }else{
            let data = this.state.filteredDataCopy;
            let filtered = data.filter(product => this.matchesSearch(product, trimmed));
            let pagedData=filtered.slice(0,this.state.N);
            this.setState({
                data: filtered,
                pagedData: pagedData,
                page:1,
                maxPage:this.maxPage(filtered,this.state.N),
                searchTerm:trimmed
            });
            this.syncUrl({ searchTerm: trimmed });
        }
    }

    matchesSearch(product, query) {
        if (!query) return true;

        const q = query.trim().toLowerCase();
        if (!q) return true;

        const sku = (product.sku || "").toLowerCase();
        const name = (product.name || "").toLowerCase();

        // ---- SKU matching ----
        let skuMatch = false;

        if (q.length === 1) {
            // starts with letter OR ends with "-<letter>"
            skuMatch =
                sku.startsWith(q) ||
                sku.endsWith("-" + q);
        } else {
            skuMatch = sku.indexOf(q) !== -1;
        }

        // ---- Name matching ----
        let nameMatch = false;

        if (q.length > 1) {
            nameMatch = name.indexOf(q) !== -1;
        }

        return skuMatch || nameMatch;
    }


}

export class ProductGalleryComponent extends BaseGalleryComponent {

    render() {

        return <Gallery
            data={this.state.data}
            pagedData={this.state.pagedData}
            onFiltersChange={this.onFiltersChange}
            baseImageUrl={this.props.baseImageUrl}
            onLike={this.onLike}
            onSortChange={this.onSortChange}
            location={this.props.location}
            industry={this.props.industry}
            showLoader={this.state.showLoader}
            onSubmitSearch={this.onSubmitSearch}
            isSignedIn={this.props.isSignedIn}
            urlVersion={this.state.urlVersion}
            currentSort={this.state.sortBy}
            searchTerm={this.state.searchTerm}
        >
            <ProductFiltersBar
                key={"pfb-" + this.state.urlVersion}
                onFiltersChange={this.onFiltersChange}
                onClearAll={this.onClearAll}
                filters={this.props.filters}
                industry={this.props.industry}
                isSignedIn={this.props.isSignedIn}
                initialFilters={this.state.initialFilterBarState}
            />
        </Gallery>
    }
}



class CollectionGalleryComponent extends BaseGalleryComponent {

    render() {

        return <Gallery
            data={this.state.data}
            onFiltersChange={this.onFiltersChange}
            baseImageUrl={this.props.baseImageUrl}
            onLike={this.onLike}
            onSortChange={this.onSortChange}
        >
            <CollectionFiltersBar onFiltersChange={this.onFiltersChange} />
        </Gallery>
    }
}